import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PabrikProvider, usePabrik, clearPabrik, GABUNGAN } from "@/context/PabrikContext";
import { PabrikSwitcher, PabrikBanner } from "@/components/app/PabrikSwitcher";
import { InstallAppButton } from "@/components/app/InstallAppButton";
import {
  LayoutDashboard, TrendingUp, TrendingDown, FileText, Factory,
  ChevronDown, LogOut, Wallet, Menu, X, Settings, Shield, Clock, UserCheck } from "lucide-react";
import { canSee, canSeeAny } from "@/lib/menus";

const API = process.env.REACT_APP_BACKEND_URL;

function usePendingCount(enabled) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    const fetchCount = async () => {
      try {
        const token = localStorage.getItem("khn_token");
        if (!token) return;
        const r = await axios.get(`${API}/api/users/pending/count`, { headers: { Authorization: `Bearer ${token}` } });
        if (live) setCount(r.data?.pending_approval || 0);
      } catch { /* ignore */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => { live = false; clearInterval(id); };
  }, [enabled]);
  return count;
}

let _bizTypesCache = null;
function useBusinessTypes() {
  const [types, setTypes] = useState(_bizTypesCache || []);
  useEffect(() => {
    if (_bizTypesCache) return;
    const token = localStorage.getItem("khn_token");
    if (!token) return;
    axios.get(`${API}/api/business-types`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { _bizTypesCache = r.data; setTypes(r.data); })
      .catch(() => {});
  }, []);
  return types;
}
export { useBusinessTypes };

const Item = ({ to, label, testid, onClick }) => (
  <NavLink to={to} end data-testid={testid} onClick={onClick}
    className={({ isActive }) => `side-sub block px-3 py-1.5 rounded-md text-[13px] ${isActive ? "active" : ""}`}>
    {label}
  </NavLink>
);

const UserRow = ({ user, onLogout, testid = "logout-btn" }) => (
  <div className="flex items-center gap-2.5">
    <span className="h-8 w-8 rounded-full bg-white/10 text-slate-100 text-xs font-semibold flex items-center justify-center shrink-0" data-testid="user-avatar">
      {(user?.name || user?.email || "?").trim().charAt(0).toUpperCase()}
    </span>
    <div className="min-w-0 flex-1">
      <div className="text-[13px] text-slate-100 truncate leading-tight">{user?.name || user?.email}</div>
      <div className="text-[11px] text-slate-500 truncate leading-tight">{user?.role}</div>
    </div>
    <button onClick={onLogout} title="Keluar" data-testid={testid}
      className="h-8 w-8 rounded-md flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition-colors duration-150"><LogOut size={15}/></button>
  </div>
);


const SidebarContent = ({ onNav, user, openGroup, setOpenGroup, pendingCount }) => {
  const isSuperAdmin = user?.role === "super_admin";
  const isAdmin = user?.role === "admin";
  const wsType = user?.workspace_type || "hostnesia";
  const bizTypes = useBusinessTypes();
  const typeLabel = bizTypes.find(t => t.code === wsType)?.label || (wsType === "arang" ? "Bisnis Pabrik Arang" : "Bisnis Online");
  const brandName = isSuperAdmin ? "Super Admin Panel" : typeLabel;
  const brandSub = isSuperAdmin ? "Global" : (user?.workspace_name || brandName);
  const pabrik = usePabrik();
  const jenis = pabrik.enabled ? pabrik.current?.jenis : null;
  const isGab = jenis === GABUNGAN;

  const Group = ({ id, icon: Icon, label, children }) => {
    const open = openGroup === id;
    return (
      <div className="mb-1">
        <button
          onClick={() => setOpenGroup(open ? null : id)}
          data-testid={`sidebar-group-${id}`}
          className={`side-link w-full flex items-center justify-between px-3 py-2 rounded-md text-[13.5px] ${open ? "text-white" : "text-slate-300"}`}
        >
          <span className="flex items-center gap-3"><Icon size={16} className={open ? "text-slate-200" : "text-slate-500"}/><span>{label}</span></span>
          <ChevronDown size={14} className={`text-slate-500 transition-transform duration-200 ${open ? "" : "-rotate-90"}`}/>
        </button>
        {open && <div className="ml-[22px] border-l border-white/[0.08] pl-2.5 py-1 space-y-0.5">{children}</div>}
      </div>
    );
  };

  return (
    <>
      <div className="px-4 pt-5 pb-4 shrink-0 space-y-4">
        <div className="flex items-center gap-2.5 px-1">
          <span className="h-8 w-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(90,174,240,0.14)" }}>
            {isSuperAdmin ? <Shield size={16} style={{color:"#5aaef0"}}/> : <Wallet size={16} style={{color:"#5aaef0"}}/>}
          </span>
          <div className="min-w-0">
            <div className="font-semibold tracking-tight truncate text-[15px] leading-tight">Keuangan HN</div>
            <div className="text-[11px] text-slate-400 truncate" data-testid="sidebar-workspace-name">{brandSub}</div>
          </div>
        </div>
        <PabrikSwitcher onDone={onNav}/>
      </div>
      <nav className="flex-1 overflow-y-auto px-2 pb-4 min-h-0 sidebar-scroll space-y-0.5">

        {isSuperAdmin && (
          <>
            <NavLink to="/superadmin/users" data-testid="nav-sa-users" onClick={onNav}
              className={({ isActive }) => `side-link flex items-center justify-between gap-3 px-3 py-2 rounded-md text-[13.5px] ${isActive ? "active text-white" : "text-slate-300"}`}>
              <span className="flex items-center gap-3"><Shield size={16}/> Manajemen User</span>
              {pendingCount > 0 && (
                <span data-testid="sa-pending-badge"
                  className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold animate-pulse">
                  {pendingCount > 99 ? "99+" : pendingCount}
                </span>
              )}
            </NavLink>
            <NavLink to="/superadmin/audit" data-testid="nav-sa-audit" onClick={onNav}
              className={({ isActive }) => `side-link flex items-center gap-3 px-3 py-2 rounded-md text-[13.5px] ${isActive ? "active text-white" : "text-slate-300"}`}>
              <FileText size={16}/> Log Aktivitas (Global)
            </NavLink>
            <NavLink to="/superadmin/security" data-testid="nav-sa-security" onClick={onNav}
              className={({ isActive }) => `side-link flex items-center gap-3 px-3 py-2 rounded-md text-[13.5px] ${isActive ? "active text-white" : "text-slate-300"}`}>
              <Shield size={16}/> Keamanan (Captcha)
            </NavLink>
            <NavLink to="/superadmin/pabrik" data-testid="nav-sa-pabrik" onClick={onNav}
              className={({ isActive }) => `side-link flex items-center gap-3 px-3 py-2 rounded-md text-[13.5px] ${isActive ? "active text-white" : "text-slate-300"}`}>
              <Factory size={16}/> Multi Pabrik (Arang)
            </NavLink>
            <NavLink to="/superadmin/business-types" data-testid="nav-sa-biztypes" onClick={onNav}
              className={({ isActive }) => `side-link flex items-center gap-3 px-3 py-2 rounded-md text-[13.5px] ${isActive ? "active text-white" : "text-slate-300"}`}>
              <Settings size={16}/> Jenis Bisnis
            </NavLink>
            <NavLink to="/pengaturan/profil" data-testid="nav-sa-profile" onClick={onNav}
              className={({ isActive }) => `side-link flex items-center gap-3 px-3 py-2 rounded-md text-[13.5px] ${isActive ? "active text-white" : "text-slate-300"}`}>
              <Settings size={16}/> Profil & Password
            </NavLink>
          </>
        )}

        {!isSuperAdmin && wsType !== "pribadi" && (
          <>
            {canSee(user,"dashboard") && (
              <NavLink to="/dashboard" data-testid="nav-dashboard" onClick={onNav}
                className={({ isActive }) => `side-link flex items-center gap-3 px-3 py-2 rounded-md text-[13.5px] ${isActive ? "active text-white" : "text-slate-300"}`}>
                <LayoutDashboard size={16}/> Dashboard
              </NavLink>
            )}

            {wsType === "arang" && canSeeAny(user, ["produksi","produksi_kayu","produksi_bakar","produksi_pembelian","produksi_transfer","produksi_bahan","produksi_briket","kategori_produk"]) && (
              <Group id="produksi" icon={Factory} label="Produksi">
                {jenis === "tungku" && canSee(user,"produksi_kayu") && <Item to="/produksi/kayu" label="Kayu Masuk" testid="nav-kayu-masuk" onClick={onNav}/>}
                {jenis === "tungku" && canSee(user,"produksi_bakar") && <Item to="/produksi/bakar" label="Batch Bakar" testid="nav-batch-bakar" onClick={onNav}/>}
                {jenis === "reseller" && canSee(user,"produksi_pembelian") && <Item to="/produksi/pembelian" label="Pembelian Arang" testid="nav-pembelian-arang" onClick={onNav}/>}
                {(jenis === "tungku" || jenis === "reseller") && pabrik.targetsBriket?.length > 0 && canSee(user,"produksi_transfer") && <Item to="/produksi/transfer" label="Transfer Stok" testid="nav-transfer-stok" onClick={onNav}/>}
                {jenis === "briket" && canSee(user,"produksi_bahan") && <Item to="/produksi/bahan" label="Bahan Masuk" testid="nav-bahan-briket" onClick={onNav}/>}
                {jenis === "briket" && canSee(user,"produksi_briket") && <Item to="/produksi/briket" label="Produksi Briket" testid="nav-produksi-briket" onClick={onNav}/>}
                {canSee(user,"produksi") && <Item to="/produksi" label="Riwayat Stok Masuk" testid="nav-produksi" onClick={onNav}/>}
                {isAdmin && !isGab && canSee(user,"kategori_produk") && <Item to="/produksi/kategori" label="Input Kategori Produk" testid="nav-kategori-produk" onClick={onNav}/>}
              </Group>
            )}

            {canSeeAny(user, ["pemasukan","kategori_pemasukan","pembeli","piutang"]) && <Group id="pemasukan" icon={TrendingUp} label="Pemasukan">
              {canSee(user,"pemasukan") && <Item to="/pemasukan" label="Daftar Pemasukan" testid="nav-pemasukan" onClick={onNav}/>}
              {canSee(user,"kategori_pemasukan") && <Item to="/pemasukan/kategori" label="Input Kategori" testid="nav-kategori-pemasukan" onClick={onNav}/>}
              {canSee(user,"pembeli") && <Item to="/pemasukan/pembeli" label="Master Data Pembeli" testid="nav-pembeli" onClick={onNav}/>}
              {wsType === "arang" && canSee(user,"piutang") && <Item to="/piutang" label="Piutang Reminder" testid="nav-piutang" onClick={onNav}/>}
            </Group>}
            {canSeeAny(user, ["pengeluaran","kategori_pengeluaran"]) && <Group id="pengeluaran" icon={TrendingDown} label="Pengeluaran">
              {canSee(user,"pengeluaran") && <Item to="/pengeluaran" label="Daftar Pengeluaran" testid="nav-pengeluaran" onClick={onNav}/>}
              {canSee(user,"kategori_pengeluaran") && <Item to="/pengeluaran/kategori" label="Input Kategori" testid="nav-kategori-pengeluaran" onClick={onNav}/>}
            </Group>}
            {canSeeAny(user, ["akun","transfer"]) && <Group id="kas_bank" icon={Wallet} label="Kas & Bank">
              {canSee(user,"akun") && <Item to="/kas-bank/akun" label="Akun Kas & Bank" testid="nav-kas-akun" onClick={onNav}/>}
              {canSee(user,"transfer") && <Item to="/kas-bank/transfer" label="Transfer Antar Akun" testid="nav-kas-transfer" onClick={onNav}/>}
            </Group>}
            {canSee(user,"hutang_piutang") && (
              <Group id="hutang_piutang" icon={Wallet} label="Utang & Piutang">
                <Item to="/hutang-piutang" label="Daftar Utang & Piutang" testid="nav-hutang-piutang" onClick={onNav}/>
                <Item to="/hutang-piutang/kategori" label="Input Kategori" testid="nav-kategori-hutang-piutang" onClick={onNav}/>
              </Group>
            )}
            {canSeeAny(user, ["laporan_semua","laba_rugi","neraca","arus_kas","dividen","pph_05","pph_normal","riwayat_bayar"]) && <Group id="laporan" icon={FileText} label="Laporan">
              {canSee(user,"laporan_semua") && <Item to="/laporan/semua" label="Semua Laporan" testid="nav-laporan-semua" onClick={onNav}/>}
              {canSee(user,"laba_rugi") && <Item to="/laporan/laba-rugi" label="Laba Rugi" testid="nav-laba-rugi" onClick={onNav}/>}
              {canSee(user,"neraca") && <Item to="/laporan/neraca" label="Neraca" testid="nav-neraca" onClick={onNav}/>}
              {canSee(user,"arus_kas") && <Item to="/laporan/arus-kas" label="Arus Kas" testid="nav-arus-kas" onClick={onNav}/>}
              {canSee(user,"dividen") && <Item to="/laporan/dividen" label="Pembagian Dividen" testid="nav-dividen" onClick={onNav}/>}
              {canSee(user,"pph_05") && <Item to="/laporan/pph-05" label="Tarif Pajak PPh 0,5%" testid="nav-pph-05" onClick={onNav}/>}
              {canSee(user,"pph_normal") && <Item to="/laporan/pph-normal" label="Tarif Pajak Normal 22%" testid="nav-pph-normal" onClick={onNav}/>}
              {canSee(user,"riwayat_bayar") && <Item to="/pajak/riwayat-bayar" label="Riwayat Bayar Pajak" testid="nav-riwayat-bayar" onClick={onNav}/>}
            </Group>}
            <Group id="pengaturan" icon={Settings} label="Pengaturan">
              <Item to="/pengaturan/profil" label="Profil & Password" testid="nav-profile" onClick={onNav}/>
              {isAdmin && canSee(user,"pengaturan") && <Item to="/pengaturan" label="Pengaturan Umum" testid="nav-settings" onClick={onNav}/>}
              {isAdmin && <Item to="/pengaturan/users" label="Manajemen User" testid="nav-users" onClick={onNav}/>}
              {isAdmin && <Item to="/pengaturan/audit" label="Log Aktivitas" testid="nav-audit" onClick={onNav}/>}
              {isAdmin && <Item to="/pengaturan/sampah" label="Kotak Sampah" testid="nav-trash" onClick={onNav}/>}
              {isAdmin && <Item to="/pengaturan/backup" label="Backup & Restore" testid="nav-backup" onClick={onNav}/>}
            </Group>
          </>
        )}

        {!isSuperAdmin && wsType === "pribadi" && (
          <>
            {canSee(user,"dashboard") && <NavLink to="/dashboard" data-testid="nav-dashboard" onClick={onNav}
              className={({ isActive }) => `side-link flex items-center gap-3 px-3 py-2 rounded-md text-[13.5px] ${isActive ? "active text-white" : "text-slate-300"}`}>
              <LayoutDashboard size={16}/> Dashboard
            </NavLink>}
            {canSeeAny(user, ["pemasukan","kategori_pemasukan"]) && <Group id="pemasukan" icon={TrendingUp} label="Pemasukan">
              {canSee(user,"pemasukan") && <Item to="/pemasukan" label="Daftar Pemasukan" testid="nav-pemasukan" onClick={onNav}/>}
              {canSee(user,"kategori_pemasukan") && <Item to="/pemasukan/kategori" label="Input Kategori" testid="nav-kategori-pemasukan" onClick={onNav}/>}
            </Group>}
            {canSeeAny(user, ["pengeluaran","kategori_pengeluaran"]) && <Group id="pengeluaran" icon={TrendingDown} label="Pengeluaran">
              {canSee(user,"pengeluaran") && <Item to="/pengeluaran" label="Daftar Pengeluaran" testid="nav-pengeluaran" onClick={onNav}/>}
              {canSee(user,"kategori_pengeluaran") && <Item to="/pengeluaran/kategori" label="Input Kategori" testid="nav-kategori-pengeluaran" onClick={onNav}/>}
            </Group>}
            {canSeeAny(user, ["akun","transfer","impor_mutasi"]) && <Group id="kas_bank" icon={Wallet} label="Kas & Bank">
              {canSee(user,"akun") && <Item to="/pribadi/akun" label="Akun / Dompet" testid="nav-akun" onClick={onNav}/>}
              {canSee(user,"transfer") && <Item to="/pribadi/transfer" label="Transfer Antar Akun" testid="nav-transfer" onClick={onNav}/>}
              {canSee(user,"impor_mutasi") && <Item to="/pribadi/impor-mutasi" label="Impor Mutasi Bank (CSV)" testid="nav-impor-mutasi" onClick={onNav}/>}
            </Group>}
            {canSeeAny(user, ["pph_op","kredit_pajak","riwayat_bayar","harta","utang","tanggungan","pajak_pengaturan"]) && <Group id="pajak_pribadi" icon={FileText} label="Pajak Pribadi">
              {canSee(user,"pph_op") && <Item to="/pribadi/pajak" label="Laporan Pajak (SPT 1770)" testid="nav-pph-op" onClick={onNav}/>}
              {canSee(user,"kredit_pajak") && <Item to="/pribadi/kredit-pajak" label="Kredit Pajak (Bukti Potong)" testid="nav-kredit-pajak" onClick={onNav}/>}
              {canSee(user,"riwayat_bayar") && <Item to="/pajak/riwayat-bayar" label="Riwayat Bayar Pajak" testid="nav-riwayat-bayar" onClick={onNav}/>}
              {canSee(user,"harta") && <Item to="/pribadi/harta" label="Daftar Harta" testid="nav-harta" onClick={onNav}/>}
              {canSee(user,"utang") && <Item to="/pribadi/utang" label="Daftar Utang" testid="nav-utang" onClick={onNav}/>}
              {canSee(user,"tanggungan") && <Item to="/pribadi/tanggungan" label="Tanggungan Keluarga" testid="nav-tanggungan" onClick={onNav}/>}
              {canSee(user,"pajak_pengaturan") && <Item to="/pribadi/pengaturan-pajak" label="Pengaturan Pajak" testid="nav-pajak-setting" onClick={onNav}/>}
            </Group>}
            {canSeeAny(user, ["laporan_semua","arus_kas"]) && <Group id="laporan" icon={FileText} label="Laporan">
              {canSee(user,"laporan_semua") && <Item to="/laporan/semua" label="Semua Laporan" testid="nav-laporan-semua" onClick={onNav}/>}
              {canSee(user,"arus_kas") && <Item to="/laporan/arus-kas" label="Arus Kas" testid="nav-arus-kas" onClick={onNav}/>}
            </Group>}
            <Group id="pengaturan" icon={Settings} label="Pengaturan">
              <Item to="/pengaturan/profil" label="Profil & Password" testid="nav-profile" onClick={onNav}/>
              {isAdmin && canSee(user,"pengaturan") && <Item to="/pengaturan" label="Pengaturan Umum" testid="nav-settings" onClick={onNav}/>}
              {isAdmin && <Item to="/pengaturan/users" label="Manajemen User" testid="nav-users" onClick={onNav}/>}
              {isAdmin && <Item to="/pengaturan/audit" label="Log Aktivitas" testid="nav-audit" onClick={onNav}/>}
              {isAdmin && <Item to="/pengaturan/sampah" label="Kotak Sampah" testid="nav-trash" onClick={onNav}/>}
              {isAdmin && <Item to="/pengaturan/backup" label="Backup & Restore" testid="nav-backup" onClick={onNav}/>}
            </Group>
          </>
        )}
      </nav>
    </>
  );
};

const MobileTab = ({ to, icon: Icon, label, testid, match }) => {
  const location = useLocation();
  const active = match ? match.some(p => location.pathname.startsWith(p)) : location.pathname === to;
  return (
    <NavLink to={to} data-testid={testid}
      className={`mobile-tab ${active?"active":"text-slate-500"} flex flex-col items-center gap-1 py-2 px-1 flex-1 min-w-0`}>
      <span className="mobile-tab-icon h-9 w-14 rounded-full flex items-center justify-center">
        <Icon size={18}/>
      </span>
      <span className="text-[10px] font-medium truncate">{label}</span>
    </NavLink>
  );
};

export default function Layout() {
  const { user } = useAuth();
  return <PabrikProvider user={user}><LayoutInner/></PabrikProvider>;
}

const GABUNGAN_BLOCK = ["add-transfer-stok-btn", "add-pemasukan-btn", "add-pengeluaran-btn", "add-produksi-btn", "add-kayu-btn", "add-beli-btn", "add-bahan-btn", "cetak-briket-btn", "mulai-bakar-btn"]
  .map((t) => `[data-testid="${t}"]`).join(", ");

function PabrikGate({ children }) {
  const p = usePabrik();
  if (!p.enabled) return children;
  if (!p.ready) return <div className="p-10 text-center text-sm text-slate-400" data-testid="pabrik-loading">Memuat data pabrik…</div>;
  if (!p.current) return (
    <div className="p-10 text-center" data-testid="pabrik-none">
      <div className="text-slate-800 font-semibold">Belum ada pabrik aktif untuk akun Anda</div>
      <div className="text-sm text-slate-500 mt-1">Hubungi Super Admin untuk mengaktifkan pabrik atau memberi akses.</div>
    </div>
  );
  const guard = (e) => {
    if (p.current.id !== GABUNGAN) return;
    if (e.target.closest?.(GABUNGAN_BLOCK)) {
      e.preventDefault(); e.stopPropagation();
      toast.error("Mode Gabungan hanya untuk melihat. Pilih salah satu pabrik untuk menambah data.");
    }
  };
  return <><PabrikBanner/><div key={p.current.id} onClickCapture={guard}>{children}</div></>;
}

const usePabrikEnabled = () => { const p = usePabrik(); return p.enabled && !!p.current; };

// Grup sidebar yang otomatis terbuka sesuai halaman aktif
function groupOfPath(p = "") {
  const rules = [[/^\/kas-bank|^\/pribadi\/(akun|transfer|impor-mutasi)/, "kas_bank"], [/^\/pribadi\//, "pajak_pribadi"],
    [/^\/(pemasukan|piutang)/, "pemasukan"], [/^\/pengeluaran/, "pengeluaran"], [/^\/produksi/, "produksi"],
    [/^\/hutang-piutang/, "hutang_piutang"], [/^\/(laporan|pajak)/, "laporan"], [/^\/pengaturan/, "pengaturan"]];
  const hit = rules.find(([rx]) => rx.test(p));
  return hit ? hit[1] : null;
}

function LayoutInner() {
  const { user, logout: doLogout, stopImpersonate } = useAuth();
  const logout = () => { clearPabrik(); doLogout(); };
  const nav = useNavigate();
  const [openDrawer, setOpenDrawer] = useState(false);
  const loc = useLocation();
  const [openGroup, setOpenGroup] = useState(() => groupOfPath(loc.pathname));
  const isSuperAdmin = user?.role === "super_admin";
  const pendingCount = usePendingCount(isSuperAdmin);
  return (
    <div className="min-h-screen flex bg-[var(--bg)]">
      {/* Desktop sidebar — fixed height layout so logout stays visible */}
      <aside className="w-64 shrink-0 bg-[var(--sidebar)] text-white flex-col hidden md:flex h-screen sticky top-0">
        <SidebarContent user={user} openGroup={openGroup} setOpenGroup={setOpenGroup} pendingCount={pendingCount}/>
        <div className="border-t border-white/[0.06] px-4 py-3 shrink-0 space-y-2">
          <InstallAppButton/>
          <UserRow user={user} onLogout={() => { logout(); nav("/login"); }}/>
        </div>
      </aside>

      {/* Mobile drawer */}
      {openDrawer && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="w-72 bg-[var(--sidebar)] text-white flex flex-col h-[100dvh] pwa-safe-y">
            <div className="flex justify-end px-3 pt-3 shrink-0"><button onClick={()=>setOpenDrawer(false)} className="h-8 w-8 rounded-md flex items-center justify-center text-slate-400 hover:bg-white/10" data-testid="drawer-close"><X size={18}/></button></div>
            <SidebarContent onNav={()=>setOpenDrawer(false)} user={user} openGroup={openGroup} setOpenGroup={setOpenGroup} pendingCount={pendingCount}/>
            <div className="border-t border-white/[0.06] px-4 py-3 shrink-0 space-y-2">
              <InstallAppButton/>
              <UserRow user={user} testid="logout-btn-m" onLogout={() => { logout(); nav("/login"); }}/>
            </div>
          </div>
          <div className="flex-1 bg-black/40" onClick={()=>setOpenDrawer(false)}/>
        </div>
      )}

      <main className="flex-1 min-w-0 content-mobile-pad">
        <div className="md:hidden bg-white/90 backdrop-blur border-b border-slate-200 flex items-center justify-between gap-2 px-3 py-2 sticky top-0 z-20 pwa-safe-top">
          <button onClick={()=>setOpenDrawer(true)} data-testid="mobile-menu-btn" className="h-9 w-9 -ml-1 rounded-md flex items-center justify-center hover:bg-slate-100"><Menu size={20}/></button>
          {usePabrikEnabled() ? <PabrikSwitcher variant="light"/> : (
            <div className="flex items-center gap-2">
              <Wallet size={18} style={{color:"#166db4"}}/>
              <span className="font-semibold">Pusat Keuangan</span>
            </div>
          )}
          <button onClick={()=>{logout();nav("/login");}} className="h-9 w-9 -mr-1 rounded-md flex items-center justify-center hover:bg-slate-100" title="Keluar" data-testid="mobile-logout-btn"><LogOut size={18} className="text-slate-500"/></button>
        </div>
        {user?.impersonated_by && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 md:px-8 py-2 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid="impersonate-banner">
            <span className="text-amber-800 flex items-center gap-2"><UserCheck size={15}/>Anda masuk sebagai <b>{user.name || user.email}</b> ({user.email}) oleh Super Admin.</span>
            <button onClick={() => { clearPabrik(); stopImpersonate(); }} data-testid="impersonate-exit"
              className="text-xs font-medium px-3 py-1 rounded-sm bg-amber-600 text-white hover:bg-amber-700">Kembali ke Super Admin</button>
          </div>
        )}
        <PabrikGate><Outlet/></PabrikGate>
      </main>

      {!isSuperAdmin && (
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-30 flex pwa-safe-bottom" data-testid="mobile-bottom-nav">
          {canSee(user,"dashboard") && <MobileTab to="/dashboard" icon={LayoutDashboard} label="Dashboard" testid="mtab-dashboard"/>}
          {canSee(user,"pemasukan") && <MobileTab to="/pemasukan" icon={TrendingUp} label="Pemasukan" testid="mtab-pemasukan" match={["/pemasukan"]}/>}
          {canSee(user,"pengeluaran") && <MobileTab to="/pengeluaran" icon={TrendingDown} label="Pengeluaran" testid="mtab-pengeluaran" match={["/pengeluaran"]}/>}
          {canSee(user,"laporan_semua") && <MobileTab to="/laporan/semua" icon={FileText} label="Laporan" testid="mtab-laporan" match={["/laporan"]}/>}
        </nav>
      )}
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="border-b border-slate-200 bg-white px-4 md:px-8 py-4 md:py-6 flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4 md:sticky md:top-0 md:z-10">
      <div>
        <h1 className="text-xl md:text-2xl font-semibold text-slate-900">{title}</h1>
        {subtitle && <p className="text-xs md:text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2 flex-wrap">{actions}</div>
    </div>
  );
}
